import { CardHeader, CardTitle } from '@folio/components';
import { Card, CardContent } from '@folio/components/ui/card';
import { DocumentsCollection } from '@folio/components/module-elements/Documents/DocumentsCollection';
import { DocumentViewer, useDocumentViewer } from '@folio/components/features/document-viewer';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  getLatestDocumentsByType,
  keyValueFieldsToObject,
  policyControllerListPolicyDocumentsQueryOptions,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Center, Stack } from '@folio/components/ui/flex';

export function RenewalDocumentsCard() {
  const { t } = useTranslation();

  const { policy, branches } = usePolicyOnDashboard();

  const { data: generatedDocuments, status: generatedDocumentsStatus } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: data =>
      data.generatedDocuments.map(({ configName, documentType }) => documentType ?? configName),
  });

  const renewalBranch = branches.data.find(
    branch => branch.branchType === 'RENEWAL' && branch.branchStatus === 'BRANCH_OPEN',
  );

  const { data: renewalDocuments, status: renewalDocumentsStatus } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id, {
      branchId: renewalBranch?.id,
    }),
    enabled: !!renewalBranch?.id,
    select: data =>
      data.data.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
    placeholderData: keepPreviousData,
  });

  const renewalCurrentDocuments = getLatestDocumentsByType(
    renewalDocuments?.filter(
      doc =>
        (doc.type === 'generated' || doc.type === 'non_delegated') &&
        doc.category &&
        doc.metadata.branchid === renewalBranch?.id &&
        renewalBranch,
    ) || [],
  );

  const categories = [
    ...new Set([
      ...(generatedDocuments ?? []),
      ...renewalCurrentDocuments.flatMap(({ type, category }) =>
        type === 'non_delegated' && category ? [category] : [],
      ),
    ]),
  ];
  const { selectedDocument, selectDocument, closeViewer } =
    useDocumentViewer(renewalCurrentDocuments);

  if (
    (renewalDocumentsStatus === 'success' && !renewalCurrentDocuments.length) ||
    renewalDocumentsStatus === 'pending' ||
    generatedDocumentsStatus === 'pending'
  )
    return null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('renewalDocuments.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack gap={1}>
            {renewalDocumentsStatus === 'error' && (
              <Center className="h-full">{t('renewalDocuments.fetchError')}</Center>
            )}
            {renewalDocumentsStatus === 'success' && (
              <DocumentsCollection
                groups={categories.map(category => ({
                  id: category,
                  label: t(`documents.${category}`),
                  documents: renewalCurrentDocuments.filter(
                    document => document.category === category,
                  ),
                }))}
                onView={selectDocument}
              />
            )}
          </Stack>
        </CardContent>
      </Card>
      <DocumentViewer
        productId={policy.productId}
        document={selectedDocument}
        documents={renewalCurrentDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
      />
    </>
  );
}
